// Shared pieces of the console's pages: page head and tabs, KPI rows (Kettle's hairline grammar),
// range picker, code blocks, tables, empty states, side sheets, meters and a host for the DOM-built
// modules (templates.ts, email-look.ts). React escapes text; nothing here sets HTML.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Delta, Unusual } from "./charts/Delta.tsx";
import { fmt, type Trend } from "./fmt.ts";
import { RANGES, type Range } from "./data/stats.ts";
import { ICONS } from "./shell/nav.tsx";
import { errText } from "./api.ts";
import type { DocumentData } from "@estra/client/firestore";

export const STATUS: Record<string, string> = { draft: "Draft", files: "Files uploaded", queued: "Setting up", provisioning: "Setting up", ready: "Live", failed: "Needs attention", deleting: "Deleting" };
export const MAIL_STATUS: Record<string, string> = { sent: "Sent", delivered: "Delivered", bounced: "Bounced", complained: "Complaint", failed: "Failed", verified: "Verified", pending: "Pending", disabled: "Disabled", queued: "Queued" };

export function Pill({ status, children }: { status?: string; children?: ReactNode }) {
  return <span className={`pill s-${status ?? ""}`}>{children ?? STATUS[status ?? ""] ?? MAIL_STATUS[status ?? ""] ?? status}</span>;
}
/** A project's pill: a live backend being re-provisioned with new files stays "Live". */
export function ProjectPill({ p }: { p: DocumentData }) {
  if (p.apiUrl && ["files", "queued", "provisioning"].includes(p.status)) return <Pill status="ready">{p.status === "files" ? "Live · changes to apply" : "Live · updating"}</Pill>;
  return <Pill status={p.status} />;
}

export function Tabs({ tabs, active, href }: { tabs: { id: string; label: string }[]; active: string; href: (id: string) => string }) {
  return (
    <nav className="tabs" aria-label="Sections">
      {tabs.map((t) => (
        <a key={t.id} href={href(t.id)} className={t.id === active ? "on" : ""} aria-current={t.id === active ? "page" : undefined}>{t.label}</a>
      ))}
    </nav>
  );
}

export function PageHead({ kicker, title, children, star }: { kicker?: ReactNode; title: ReactNode; children?: ReactNode; star?: { on: boolean; toggle: () => void } }) {
  return (
    <header className="head">
      {kicker && <div className="kicker">{kicker}</div>}
      <h1>
        {title}
        {star && <button className={`star${star.on ? " on" : ""}`} onClick={star.toggle} aria-pressed={star.on} title={star.on ? "Remove from shortcuts" : "Add to shortcuts"}>{star.on ? "★" : "☆"}</button>}
      </h1>
      {children && <div className="actions">{children}</div>}
    </header>
  );
}

export function RangePicker({ value, onChange, ranges = RANGES }: { value: Range; onChange: (r: Range) => void; ranges?: Range[] }) {
  return (
    <div className="seg" role="radiogroup" aria-label="Time range">
      {ranges.map((r) => (
        <button key={r} role="radio" aria-checked={r === value} className={r === value ? "on" : ""} onClick={() => onChange(r)}>{r}</button>
      ))}
    </div>
  );
}

/** Counts up to a number when it first appears (respecting reduced motion). */
export function Tween({ value, format = fmt.int }: { value: number; format?: (n: number) => string }) {
  const [shown, setShown] = useState(value);
  const from = useRef(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return void setShown(value);
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / 700);
      const e = 1 - (1 - k) ** 3;
      setShown(a + (value - a) * e);
      if (k < 1) raf = requestAnimationFrame(step);
      else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{format(shown)}</>;
}

export interface StatDef {
  label: string;
  value: number | null | undefined;
  format?: (n: number) => string;
  trend?: Trend;
  good?: "up" | "down" | "none";
  note?: ReactNode;
  href?: string;
  /** "vs the 7 days before" etc. */
  vs?: string;
}
export function Stats({ items, quiet }: { items: StatDef[]; quiet?: boolean }) {
  return (
    <div className={`stats${quiet ? " quiet" : ""}`} style={{ ["--cols" as string]: Math.min(items.length, 4) }}>
      {items.map((s) => {
        const f = s.format ?? fmt.int;
        const body = (
          <>
            <span className="stat-label">{s.label}</span>
            <span className="stat-value">{s.value === null || s.value === undefined ? "–" : <Tween value={s.value} format={f} />}</span>
            <span className="stat-line">
              {s.trend && <Delta now={s.trend.now} prev={s.trend.prev} good={s.good ?? "up"} format={f} label={s.vs ?? "the period before"} />}
              {s.trend && <Unusual t={s.trend} good={s.good ?? "up"} format={f} min={20} />}
              {s.note && <span className="stat-note">{s.note}</span>}
            </span>
          </>
        );
        return s.href ? <a key={s.label} className="stat" href={s.href}>{body}</a> : <div key={s.label} className="stat">{body}</div>;
      })}
    </div>
  );
}

export function CodeBlock({ code, lang }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="code">
      <span className="lang">{lang ?? ""}</span>
      <button className="copy" type="button" onClick={async () => { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1400); }}>{copied ? "Copied" : "Copy"}</button>
      <pre><code>{code}</code></pre>
    </div>
  );
}

export function Card({ title, sub, children, actions, className }: { title?: ReactNode; sub?: ReactNode; children?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <section className={`card${className ? ` ${className}` : ""}`}>
      {(title || actions) && (
        <div className="card-head">
          <div>{title && <h2>{title}</h2>}{sub && <p className="muted small">{sub}</p>}</div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function Table({ head, rows, empty, onRow }: { head: ReactNode[]; rows: ReactNode[][]; empty?: ReactNode; onRow?: (i: number) => void }) {
  if (!rows.length) return <p className="muted small">{empty ?? "Nothing yet."}</p>;
  return (
    <div className="table-wrap">
      <table className="t">
        <thead><tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={onRow ? "click" : undefined} onClick={onRow ? () => onRow(i) : undefined}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Empty({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p className="small">{children}</p>}
      {action}
    </div>
  );
}

export const Skeleton = ({ h = 120, w = "100%" }: { h?: number; w?: number | string }) => <div className="skel" style={{ height: h, width: w }} />;
export const Loading = () => (
  <div className="stack" aria-busy="true">
    <Skeleton h={34} w="40%" />
    <Skeleton h={90} />
    <Skeleton h={220} />
  </div>
);
export const Failed = ({ error, retry }: { error: unknown; retry?: () => void }) => (
  <div className="notice bad">
    {errText(error)} {retry && <button className="linkish" onClick={retry}>Try again</button>}
  </div>
);

export function Sheet({ title, onClose, children }: { title: ReactNode; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <aside className="sheet" role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}>
        <div className="sheet-head"><h2>{title}</h2><button className="icon-btn" onClick={onClose} aria-label="Close">{ICONS.close}</button></div>
        {children}
      </aside>
    </>
  );
}

/** Runs an async action with a busy flag and a message line. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; bad?: boolean } | null>(null);
  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      if (done) setMsg({ text: done });
    } catch (e) {
      setMsg({ text: errText(e), bad: true });
    }
    setBusy(false);
  };
  const note = msg ? <span className={`small ${msg.bad ? "bad" : "muted"}`}>{msg.text}</span> : null;
  return { busy, run, note, setMsg };
}

/** Hosts a DOM-built module (templates.ts, email-look.ts) inside React. */
export function Legacy({ mount, deps = [] }: { mount: (el: HTMLElement) => void | Promise<void>; deps?: unknown[] }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.replaceChildren();
    void Promise.resolve(mount(ref.current)).catch((e) => ref.current?.replaceChildren(Object.assign(document.createElement("p"), { className: "bad", textContent: errText(e) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return <div className="legacy" ref={ref} />;
}

/** One bar per item, sorted by the caller: a name, a value and a hairline bar (Kettle's BarList). */
export function BarList({ items, format = fmt.int, empty, color }: { items: { dim: string; value: number }[]; format?: (n: number) => string; empty?: ReactNode; color?: string }) {
  if (!items.length) return <p className="muted small">{empty ?? "Nothing yet."}</p>;
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="barlist">
      {items.map((i) => (
        <li key={i.dim}>
          <span className="bl-name mono">{i.dim}</span>
          <span className="bl-val">{format(i.value)}</span>
          <span className="bl-bar"><i style={{ width: `${(100 * i.value) / max}%`, background: color }} /></span>
        </li>
      ))}
    </ul>
  );
}

/** Beta allowance meters: one hairline bar per daily amount; red only once past 100% (shown, not enforced). */
export function Allowance({ rows }: { rows: { label: string; used: number; limit: number; format?: (n: number) => string; note?: string }[] }) {
  return (
    <ul className="allowance">
      {rows.map((r) => {
        const f = r.format ?? fmt.int;
        const pct = r.limit ? (100 * r.used) / r.limit : 0;
        return (
          <li key={r.label} className={`al-row${pct > 100 ? " is-past" : ""}`}>
            <div className="al-head">
              <span className="al-label">{r.label}</span>
              <span className="al-num">{f(r.used)} of {f(r.limit)} a day<strong>{Math.round(pct)}%</strong></span>
            </div>
            <div className="al-bar" role="meter" aria-valuemin={0} aria-valuemax={r.limit} aria-valuenow={r.used} aria-label={r.label}>
              <span className="al-used" style={{ width: `${Math.min(100, pct)}%` }} />
              {Array.from({ length: 9 }, (_, i) => <span key={i} className="al-tick" style={{ left: `${(i + 1) * 10}%` }} />)}
            </div>
            {r.note && <span className="al-note">{r.note}</span>}
          </li>
        );
      })}
    </ul>
  );
}

/** Requests per minute for the last hour, newest on the right (Kettle's Now strip). */
export function MinuteStrip({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  return (
    <div className="minutes" role="img" aria-label={`Requests per minute over the last hour; latest ${values[values.length - 1] ?? 0}`}>
      {values.map((v, i) => <i key={i} style={{ height: `${Math.max(2, (100 * v) / max)}%` }} title={`${values.length - 1 - i} min ago: ${v}`} />)}
    </div>
  );
}

/** Copied in spirit from Kettle's Celebrate: a banner with a burst and a little confetti, once. */
export function Celebrate({ title, sub, onClose }: { title: ReactNode; sub?: ReactNode; onClose: () => void }) {
  const colors = ["var(--s1)", "var(--s2)", "var(--s3)", "var(--s4)", "var(--s5)", "var(--ember)"];
  return (
    <div className="celebrate" role="status">
      <div className="confetti" aria-hidden="true">
        {Array.from({ length: 22 }, (_, i) => <i key={i} style={{ left: `${(i * 37) % 100}%`, background: colors[i % colors.length], animationDelay: `${(i % 7) * 0.12}s` }} />)}
      </div>
      <span className="burst" aria-hidden="true" />
      <div><h3>{title}</h3>{sub && <p className="muted small">{sub}</p>}</div>
      <button className="icon-btn x" onClick={onClose} aria-label="Dismiss">{ICONS.close}</button>
    </div>
  );
}

/** Two-step confirm inside a row: the first click asks, the second does it. */
export function ConfirmButton({ label, confirm, onConfirm, danger, small = true, disabled }: { label: string; confirm: string; onConfirm: () => void | Promise<void>; danger?: boolean; small?: boolean; disabled?: boolean }) {
  const [asking, setAsking] = useState(false);
  const cls = `btn ${danger ? "btn-danger" : "btn-ghost"}${small ? " btn-small" : ""}`;
  if (!asking) return <button className={cls} disabled={disabled} onClick={() => setAsking(true)}>{label}</button>;
  return (
    <span className="row">
      <span className="small muted">{confirm}</span>
      <button className={`btn${danger ? " btn-danger" : ""} btn-small`} onClick={async () => { setAsking(false); await onConfirm(); }}>Yes</button>
      <button className="btn btn-ghost btn-small" onClick={() => setAsking(false)}>Cancel</button>
    </span>
  );
}
